import React, { useEffect, useMemo, useRef, useState } from "react";

import { COUNTRY_LIST, flagEmoji } from "../../../../constants/countries";
import { cn } from "../../../common/u1/cn";
import { CONTROL, controlBorder, Field, MENU, MENU_ROW } from "./TradeField";
import { ChevronDown } from "../../../../assets/ui/icons";

/* Dial code + mobile, mirroring the AgentIntake "Nominee" phone control.
 *
 * The masks and `formatPhone` are transcribed unchanged — they encode which
 * shape a number takes per country, which is business rule, not presentation.
 */
const PHONE_MASKS = {
  "+971": "XX XXX XXXX",
  "+966": "XX XXX XXXX",
  "+974": "XXXX XXXX",
  "+965": "XXXX XXXX",
  "+973": "XXXX XXXX",
  "+968": "XXXX XXXX",
  "+91": "XXXXX XXXXX",
  "+44": "XXXX XXXXXX",
  "+1": "(XXX) XXX-XXXX",
};

export function formatPhone(raw, dial) {
  const mask = PHONE_MASKS[dial] || "";
  const digits = (raw || "").replace(/\D/g, "");
  if (!mask || !digits) return raw || "";
  let out = "";
  let i = 0;
  for (const m of mask) {
    if (i >= digits.length) break;
    out += m === "X" ? digits[i++] : m;
  }
  return out;
}

export const DialPhoneField = ({
  dial = "+971",
  onDialChange = () => {},
  phone,
  onPhoneChange = () => {},
  error,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapRef = useRef(null);

  const selected = useMemo(() => COUNTRY_LIST.find((c) => c.dial === dial), [dial]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return COUNTRY_LIST;
    return COUNTRY_LIST.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.dial.includes(q) ||
        c.code.toLowerCase().includes(q)
    );
  }, [search]);

  /* The live control closes on a click-away layer only. Escape closes it too —
     a menu you can open from the keyboard has to be closeable from it. */
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const example = (PHONE_MASKS[dial] || "").replace(/X/g, "0");

  const pickDial = (c) => {
    onDialChange(c.dial);
    setOpen(false);
    setSearch("");
  };

  return (
    <div ref={wrapRef} className="grid grid-cols-[150px_1fr] gap-2.5">
      <Field id="trade-dial" label="Country code">
        <div className="relative">
          <button
            type="button"
            id="trade-dial"
            onClick={() => setOpen((o) => !o)}
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-label={`Country code: ${selected?.name || dial}`}
            className={cn(
              CONTROL,
              controlBorder(error),
              "flex cursor-pointer items-center justify-between gap-2"
            )}
          >
            <span className="flex items-center gap-2 overflow-hidden">
              <span className="text-[16px]" aria-hidden="true">
                {selected ? flagEmoji(selected.code) : "🏳️"}
              </span>
              <span className="font-semibold">{dial}</span>
            </span>
            <span className="text-[var(--trade-micro)]">
              <ChevronDown open={open} />
            </span>
          </button>

          {open && (
            <>
              {/* Click-away layer, as the live control has. */}
              <div
                aria-hidden="true"
                onClick={() => setOpen(false)}
                className="fixed inset-0 z-[35]"
              />
              <div role="listbox" className={cn(MENU, "w-[300px] max-w-[80vw] p-2")}>
                <input
                  autoFocus
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search code / country…"
                  aria-label="Search country or dial code"
                  className={cn(CONTROL, "mb-1.5 border-[var(--slate-3)]")}
                />
                <div className="max-h-[220px] overflow-y-auto">
                  {filtered.map((c) => (
                    <button
                      type="button"
                      key={c.code}
                      role="option"
                      aria-selected={c.dial === dial}
                      onClick={() => pickDial(c)}
                      className={cn(
                        MENU_ROW,
                        "py-2",
                        c.dial === dial && "bg-[var(--dial-active)]"
                      )}
                    >
                      <span className="text-[16px]" aria-hidden="true">
                        {flagEmoji(c.code)}
                      </span>
                      <span className="min-w-0 grow basis-0 truncate">{c.name}</span>
                      <span className="font-semibold text-[var(--trade-intro)]">{c.dial}</span>
                    </button>
                  ))}
                  {filtered.length === 0 && (
                    <div className="p-2.5 text-[13px] text-[var(--trade-micro)] font-sans">
                      No matches
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </Field>

      <Field id="trade-phone" label="Mobile">
        <input
          id="trade-phone"
          value={formatPhone(phone, dial)}
          onChange={(e) => onPhoneChange(formatPhone(e.target.value, dial))}
          placeholder={example || "Phone number"}
          inputMode="tel"
          aria-invalid={error ? true : undefined}
          className={cn(CONTROL, controlBorder(error), "focus:border-[var(--orange)]")}
        />
      </Field>
    </div>
  );
};

export default DialPhoneField;
